// Supabase API integration for Crown of Suns game
// Connects to our 50 Edge Functions deployed on ciqowbqbtaluohujwiil.supabase.co

import { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from './supabase'

export interface ApiResponse<T> {
  data?: T
  error?: string
  status: number
}

// Game entity interfaces
export interface Player {
  id: string
  name: string
  level: number
  credits: number
  fleet_power: number
  planets: number
  alliances: number
  created_at: string
  resources?: {
    crystals?: number
    metals?: number
    energy?: number
    alloys?: number
  }
}

export interface Fleet {
  id: string
  name: string
  player_id: string
  ships: any[] | number  // Support both array format and simple count
  power: number
  location: string
  status: string
  destination?: string
  eta?: string
}

export interface Planet {
  id: string
  name: string
  player_id: string
  system_id: string
  type: string
  population: number
  infrastructure: number
  resources: {
    minerals: number
    energy: number
    food: number
  }
}

export interface Research {
  id: string
  player_id: string
  technology: string
  level: number
  progress: number
  research_points: number
}

export interface Alliance {
  id: string
  name: string
  leader_id: string
  members: number
  power: number
  diplomacy_status: string
}

export interface Market {
  id: string
  item_type: string
  quantity: number
  price: number
  seller_id: string
  location: string
}

class SupabaseAPI {
  private static instance: SupabaseAPI
  private supabase: SupabaseClient
  private authToken: string | null = null
  
  constructor() {
    this.supabase = supabase
  }

  static getInstance(): SupabaseAPI {
    if (!SupabaseAPI.instance) {
      SupabaseAPI.instance = new SupabaseAPI()
    }
    return SupabaseAPI.instance
  }

  // Token management for authentication
  setToken(token: string | null): void {
    this.authToken = token
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
  }

  getToken(): string | null {
    if (!this.authToken) {
      this.authToken = localStorage.getItem('auth_token')
    }
    return this.authToken
  }

  private async callFunction<T>(
    functionName: string, 
    data?: any
  ): Promise<ApiResponse<T>> {
    try {
      const { data: result, error } = await this.supabase.functions.invoke(
        functionName, 
        { body: data }
      )

      if (error) {
        return {
          error: error.message,
          status: 400
        }
      }

      return {
        data: result,
        status: 200
      }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
        status: 0
      }
    }
  }

  // ===========================================
  // PLAYER MANAGEMENT - 5 Functions
  // ===========================================
  
  async getPlayer(playerId: string): Promise<ApiResponse<Player>> {
    return this.callFunction<Player>('player-get', { id: playerId })
  }

  async getPlayers(): Promise<ApiResponse<Player[]>> {
    return this.callFunction<Player[]>('player-list')
  }

  async createPlayer(playerData: Partial<Player>): Promise<ApiResponse<Player>> {
    return this.callFunction<Player>('player-create', playerData)
  }

  async updatePlayer(playerId: string, playerData: Partial<Player>): Promise<ApiResponse<Player>> {
    return this.callFunction<Player>('player-update', { id: playerId, ...playerData })
  }

  async deletePlayer(playerId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.callFunction<{ success: boolean }>('player-delete', { id: playerId })
  }

  // ===========================================
  // FLEET MANAGEMENT - 5 Functions
  // ===========================================

  async getFleets(playerId?: string): Promise<ApiResponse<Fleet[]>> {
    return this.callFunction<Fleet[]>('fleet-list', playerId ? { player_id: playerId } : undefined)
  }

  async getFleet(fleetId: string): Promise<ApiResponse<Fleet>> {
    return this.callFunction<Fleet>('fleet-get', { id: fleetId })
  }

  async createFleet(fleetData: Partial<Fleet>): Promise<ApiResponse<Fleet>> {
    return this.callFunction<Fleet>('fleet-create', fleetData)
  }

  async updateFleet(fleetId: string, fleetData: Partial<Fleet>): Promise<ApiResponse<Fleet>> {
    return this.callFunction<Fleet>('fleet-update', { id: fleetId, ...fleetData })
  }

  async deleteFleet(fleetId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.callFunction<{ success: boolean }>('fleet-delete', { id: fleetId })
  }

  // ===========================================
  // PLANET MANAGEMENT - 5 Functions
  // ===========================================

  async getPlanets(playerId?: string): Promise<ApiResponse<Planet[]>> {
    return this.callFunction<Planet[]>('planet-list', playerId ? { player_id: playerId } : undefined)
  }

  async getPlanet(planetId: string): Promise<ApiResponse<Planet>> {
    return this.callFunction<Planet>('planet-get', { id: planetId })
  }

  async createPlanet(planetData: Partial<Planet>): Promise<ApiResponse<Planet>> {
    return this.callFunction<Planet>('planet-create', planetData)
  }

  async updatePlanet(planetId: string, planetData: Partial<Planet>): Promise<ApiResponse<Planet>> {
    return this.callFunction<Planet>('planet-update', { id: planetId, ...planetData })
  }

  async deletePlanet(planetId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.callFunction<{ success: boolean }>('planet-delete', { id: planetId })
  }

  // ===========================================
  // RESEARCH MANAGEMENT - 5 Functions
  // ===========================================

  async getResearch(playerId: string): Promise<ApiResponse<Research[]>> {
    return this.callFunction<Research[]>('research-list', { player_id: playerId })
  }

  async getResearchItem(researchId: string): Promise<ApiResponse<Research>> {
    return this.callFunction<Research>('research-get', { id: researchId })
  }

  async createResearch(researchData: Partial<Research>): Promise<ApiResponse<Research>> {
    return this.callFunction<Research>('research-create', researchData)
  }

  async updateResearch(researchId: string, researchData: Partial<Research>): Promise<ApiResponse<Research>> {
    return this.callFunction<Research>('research-update', { id: researchId, ...researchData })
  }

  async deleteResearch(researchId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.callFunction<{ success: boolean }>('research-delete', { id: researchId })
  }

  // ===========================================
  // ALLIANCE MANAGEMENT - 5 Functions
  // ===========================================

  async getAlliances(): Promise<ApiResponse<Alliance[]>> {
    return this.callFunction<Alliance[]>('alliance-list')
  }

  async getAlliance(allianceId: string): Promise<ApiResponse<Alliance>> {
    return this.callFunction<Alliance>('alliance-get', { id: allianceId })
  }

  async createAlliance(allianceData: Partial<Alliance>): Promise<ApiResponse<Alliance>> {
    return this.callFunction<Alliance>('alliance-create', allianceData)
  }

  async updateAlliance(allianceId: string, allianceData: Partial<Alliance>): Promise<ApiResponse<Alliance>> {
    return this.callFunction<Alliance>('alliance-update', { id: allianceId, ...allianceData })
  }

  async deleteAlliance(allianceId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.callFunction<{ success: boolean }>('alliance-delete', { id: allianceId })
  }

  // ===========================================
  // MARKET MANAGEMENT - 5 Functions
  // ===========================================

  async getMarket(): Promise<ApiResponse<Market[]>> {
    return this.callFunction<Market[]>('market-list')
  }

  async getMarketItem(itemId: string): Promise<ApiResponse<Market>> {
    return this.callFunction<Market>('market-get', { id: itemId })
  }

  async createMarketItem(itemData: Partial<Market>): Promise<ApiResponse<Market>> {
    return this.callFunction<Market>('market-create', itemData)
  }

  async updateMarketItem(itemId: string, itemData: Partial<Market>): Promise<ApiResponse<Market>> {
    return this.callFunction<Market>('market-update', { id: itemId, ...itemData })
  }

  async deleteMarketItem(itemId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.callFunction<{ success: boolean }>('market-delete', { id: itemId })
  }

  // ===========================================
  // GAME SYSTEMS - 20 Additional Functions
  // ===========================================

  // Battle System
  async getBattles(playerId?: string): Promise<ApiResponse<any[]>> {
    return this.callFunction<any[]>('battle-list', playerId ? { player_id: playerId } : undefined)
  }

  async createBattle(battleData: any): Promise<ApiResponse<any>> {
    return this.callFunction<any>('battle-create', battleData)
  }

  // Diplomacy System
  async getDiplomacy(playerId: string): Promise<ApiResponse<any[]>> {
    return this.callFunction<any[]>('diplomacy-list', { player_id: playerId })
  }

  async createDiplomacy(diplomacyData: any): Promise<ApiResponse<any>> {
    return this.callFunction<any>('diplomacy-create', diplomacyData)
  }

  // Trade System
  async getTrades(playerId?: string): Promise<ApiResponse<any[]>> {
    return this.callFunction<any[]>('trade-list', playerId ? { player_id: playerId } : undefined)
  }

  async createTrade(tradeData: any): Promise<ApiResponse<any>> {
    return this.callFunction<any>('trade-create', tradeData)
  }

  // Communication System
  async getMessages(playerId: string): Promise<ApiResponse<any[]>> {
    return this.callFunction<any[]>('communication-list', { player_id: playerId })
  }

  async sendMessage(messageData: any): Promise<ApiResponse<any>> {
    return this.callFunction<any>('communication-create', messageData)
  }

  // Notification System
  async getNotifications(playerId: string): Promise<ApiResponse<any[]>> {
    return this.callFunction<any[]>('notification-list', { player_id: playerId })
  }

  async createNotification(notificationData: any): Promise<ApiResponse<any>> {
    return this.callFunction<any>('notification-create', notificationData)
  }

  async updateNotification(notificationId: string, notificationData: any): Promise<ApiResponse<any>> {
    return this.callFunction<any>('notification-update', { id: notificationId, ...notificationData })
  }

  // Analytics & Reporting
  async getAnalytics(playerId: string): Promise<ApiResponse<any>> {
    return this.callFunction<any>('analytics-get', { player_id: playerId })
  }

  async getReports(): Promise<ApiResponse<any[]>> {
    return this.callFunction<any[]>('reports-list')
  }

  // Page Settings Integration
  async getPageSettings(): Promise<ApiResponse<any>> {
    return this.callFunction<any>('page-settings-get')
  }

  async updatePageSettings(settings: any): Promise<ApiResponse<any>> {
    return this.callFunction<any>('page-settings-update', settings)
  }

  // Health & Monitoring
  async healthCheck(): Promise<ApiResponse<{ status: string, timestamp: string }>> {
    return this.callFunction<{ status: string, timestamp: string }>('health-check')
  }

  async getSystemStatus(): Promise<ApiResponse<any>> {
    return this.callFunction<any>('system-status')
  }

  // Authentication & User Management
  async login(credentials: { email: string, password: string }): Promise<ApiResponse<any>> {
    try {
      const { data, error } = await this.supabase.auth.signInWithPassword(credentials)
      
      if (error) {
        return { error: error.message, status: 400 }
      }

      return { data: data.user, status: 200 }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Authentication error',
        status: 0
      }
    }
  }

  async logout(): Promise<ApiResponse<{ success: boolean }>> {
    try {
      const { error } = await this.supabase.auth.signOut()
      
      if (error) {
        return { error: error.message, status: 400 }
      }

      return { data: { success: true }, status: 200 }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Logout error',
        status: 0
      }
    }
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    try {
      const { data: { user }, error } = await this.supabase.auth.getUser()
      
      if (error) {
        return { error: error.message, status: 400 }
      }

      return { data: user, status: 200 }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'User fetch error',
        status: 0
      }
    }
  }

  // Real-time subscriptions
  subscribeToChanges(table: string, callback: (payload: any) => void) {
    return this.supabase
      .channel(`public:${table}`)
      .on('postgres_changes', { event: '*', schema: 'public', table }, callback)
      .subscribe()
  }

  unsubscribe(subscription: any) {
    return this.supabase.removeChannel(subscription)
  }
}

export default SupabaseAPI